import { NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    input,
    linkedSignal,
    output,
    Signal,
    signal,
    WritableSignal,
    inject,
    viewChild,
    TemplateRef,
    untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Item } from "../../pojo/Item";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { TypedTemplateDirective } from "../../standalone/typed.template.directive";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25ItemComponent } from "../s25-item/s25.item.component";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { MeetingPatternGrid, MeetingPatternGridService } from "../s25-swarm-schedule/meeting.pattern.grid.service";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { Bind } from "../../decorators/bind.decorator";
import { SwarmSchedule } from "../s25-swarm-schedule/SwarmSchedule";
import { S25Util } from "../../util/s25-util";
import { UserStore } from "../../stores/user.store";
import { DowGrid, S25DowGridComponent } from "../s25-virtual-grid/s25.dow.grid.component";
import { DowGridUtil } from "../s25-virtual-grid/s25.dow.grid.util";
import { Proto } from "../../pojo/Proto";
import { BoardUploadService } from "../s25-swarm-schedule/board.upload.service";
import { S25VirtualGridModule } from "../s25-virtual-grid/s25.virtual.grid.module";
import { S25MeetingPatternGridEventComponent } from "./s25.meeting.pattern.grid.event.component";
import { MPGUtil } from "./s25.meeting.pattern.grid.util";
import EpochTimestamp = Proto.EpochTimestamp;
import Milliseconds = Proto.Milliseconds;
import { S25Const } from "../../util/s25-const";
import { CacheRepository } from "../../decorators/cache.decorator";
import { BOARD_CONST } from "../s25-swarm-schedule/s25.board.const";
import { Throttle } from "../../decorators/debounce.decorator";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { BoardService } from "../s25-swarm-schedule/s25.board.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { AriaLive } from "../../services/aria.live.service";
import { Telemetry } from "../../decorators/telemetry.decorator";
import { s25Resource } from "../../util/s25.resource";
import { LoginStore } from "../../stores/login.store";
import { InstanceCount } from "../../util/s25.instance.count";

@Component({
    selector: "s25-ng-meeting-pattern-grid",
    template: `
        <h2 class="board-name">{{ board().boardName }}</h2>
        @if (status.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (errorMessage(); as message) {
            <div class="ngRed">{{ message }}</div>
        } @else if (status.hasValue()) {
            <s25-ng-dow-pattern-grid
                #dowGrid
                [items]="items()"
                [rowHeaders]="rows()"
                [displayMessage]="errorMessage()"
                [interactionHandlers]="interactionHandlers"
                [domainEquals]="domainEquals"
                [itemInitialized]="onGridItemInitialized"
                [itemProjector]="itemProjector"
                [dows]="BOARD_CONST.allDows"
                [visibleDows]="visibleDows()"
                [startHour]="startHour()"
                [endHour]="endHour()"
                [rowHeaderTemplate]="defaultRowHeaderTmpl()"
                [itemTemplate]="defaultItemTmpl()"
                [decorationTemplate]="decorationTmpl()"
                [optionsLeftTemplate]="optionsLeftTmpl()"
                [optionsRightTemplate]="optionsRightTmpl()"
                [optionsBelowTemplate]="optionsBelowTmpl()"
                [canDrag]="{ x: canDragHorizontally(), y: true }"
                [hasMinimap]="true"
                [hasUndo]="true"
                [displayShadows]="true"
                [findOverlap]="true"
                [createOverlapItems]="true"
                [dragParticipantsResolver]="dragParticipantsResolver"
            />
        }

        <ng-template #optionsLeft [template]="optionsLeftTmpl()" let-defaultOptions="defaultOptions">
            <button class="aw-button aw-button--danger--outline exit" (click)="exit.emit(false)">Exit</button>
            @if (!board().shared) {
                <button class="aw-button aw-button--outline share" (click)="openShareModal()">Share</button>
            }
            <ng-container [ngTemplateOutlet]="defaultOptions" />
        </ng-template>

        <ng-template #optionsRight [template]="optionsRightTmpl()" let-defaultOptions="defaultOptions">
            <ng-container [ngTemplateOutlet]="defaultOptions" />
            <button class="aw-button aw-button--outline toggle-filters" (click)="toggleFilters()">
                <s25-ng-icon [type]="filters().show ? 'caretUp' : 'caretDown'" [ariaLabel]="false" />
                {{ filters().show ? "Hide" : "Show" }} Location Filters
            </button>
            @if (sortOrder() !== "default") {
                <s25-ng-button
                    [type]="'outline'"
                    [isLoading]="loading().sort"
                    (clicked)="resetSort()"
                    class="reset-sort"
                >
                    Reset Sort
                </s25-ng-button>
            }
            @if (board().isOwner) {
                <s25-ng-button [type]="'primary'" [onClick]="onSave" class="send-to-optimizer">
                    Send to Optimizer
                </s25-ng-button>
            }
        </ng-template>

        <ng-template #optionsBelow [template]="optionsBelowTemplate()">
            <!-- NOTE: display=none is used to avoid recalculating styles when we show the filters -->
            <div class="filters" [style.display]="filters().show ? 'flex' : 'none'">
                <label class="ngBold">Filters:</label>
                <div class="options">
                    <div class="name">
                        <label for="location-name-filter">Name</label>
                        <input
                            [ngModel]="filters().name"
                            (ngModelChange)="updateFilter('name', $event)"
                            (keydown.enter)="runFilters()"
                            id="location-name-filter"
                            type="text"
                            class="c-input"
                        />
                    </div>
                    <div class="capacity">
                        <label>Capacity</label>
                        <div class="capacity-value">
                            <label for="location-capacity-filter-between" class="above">between</label>
                            <input
                                [ngModel]="filters().capacityMin"
                                (ngModelChange)="updateFilter('capacityMin', $event)"
                                (keydown.enter)="runFilters()"
                                id="location-capacity-filter-between"
                                type="number"
                                min="0"
                                class="c-input"
                            />
                        </div>
                        <div class="capacity-value">
                            <label for="location-capacity-filter-and" class="above">and</label>
                            <input
                                [ngModel]="filters().capacityMax"
                                (ngModelChange)="updateFilter('capacityMax', $event)"
                                (keydown.enter)="runFilters()"
                                id="location-capacity-filter-and"
                                type="number"
                                min="0"
                                class="c-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label for="location-features-filter" class="above"
                            >{{ filters().features.length }} Selected</label
                        >
                        <s25-ng-multiselect-search-criteria
                            [type]="'locationFeatures'"
                            [selectedItems]="filters().features"
                            [popoverOnBody]="true"
                            (changed)="updateFilter('features', $event)"
                        />
                    </div>
                    <div>
                        <label for="location-partitions-filter" class="above"
                            >{{ filters().partitions.length }} Selected</label
                        >
                        <s25-ng-multiselect-search-criteria
                            [type]="'locationPartitions'"
                            [selectedItems]="filters().partitions"
                            [popoverOnBody]="true"
                            (changed)="updateFilter('partitions', $event)"
                        />
                    </div>
                </div>
                <div class="buttons">
                    <s25-ng-button [type]="'outline'" [isLoading]="loading().filter" (clicked)="runFilters()">
                        Run Filters
                    </s25-ng-button>
                    <button class="aw-button aw-button--danger--outline clear-filters" (click)="clearFilters()">
                        Clear Filters
                    </button>
                </div>
            </div>
        </ng-template>

        <ng-template #defaultRowHeaderTemplate [template]="defaultRowHeaderTmpl()" let-header="header">
            <div class="row-header">
                @if (header.id === "-1") {
                    <span>{{ header.mpg.name }}</span>
                } @else {
                    <s25-item
                        [itemId]="header.id"
                        [itemTypeId]="Item.Ids.Location"
                        [itemName]="header.mpg.name"
                        [newTab]="true"
                        class="ngAnchor"
                    />
                }
            </div>
        </ng-template>

        <ng-template
            #defaultItemTemplate
            [template]="defaultItemTmpl()"
            let-item="item"
            let-projection="projection"
            let-dragging="dragging"
            let-moving="moving"
        >
            <s25-ng-meeting-pattern-grid-event
                [item]="item"
                [projection]="projection"
                [dragging]="dragging"
                [moving]="moving"
                [sortedBy]="sortOrder() === 'relevance' && item === relevanceSortItem()"
                (sortByRelevance)="onSortByRelevance($event)"
            />
        </ng-template>

        <ng-template #decorationTemplate [template]="decorationTmpl()" let-decoration="decoration">
            @if (decoration.data.type === "aggregate-overlap") {
                <div class="overlapItem"></div>
            }
        </ng-template>

        <s25-ng-modal #shareModal [title]="'Share Board'" [type]="'save'" [size]="'xs'" (save)="onShareSave()">
            <ng-template #s25ModalBody>
                <div class="share-modal">
                    <div class="shareWith">
                        <label class="ngBold" [id]="uniqueId"> Share With: </label>
                        <s25-ng-dropdown-search-criteria
                            [type]="'contacts'"
                            [customFilterValue]="'&is_r25user=1&itemName=username'"
                            [noSelection]="'Select User'"
                            [ariaLabel]="{ by: uniqueId }"
                            (valueChange)="addSharedUser($any($event))"
                        />
                    </div>
                    <div class="users">
                        <label class="ngBold">Username</label>
                        <div></div>
                        @for (sharee of modalSharedUsers(); track sharee.username) {
                            <div>{{ sharee.username }}</div>
                            <button
                                class="aw-button aw-button--danger--outline unshare"
                                (click)="removeSharedUser($index)"
                            >
                                Unshare
                            </button>
                        }
                    </div>
                </div>
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #overlapModal [title]="'Overlapping Sections'" [type]="'acknowledge'" [size]="'xs'">
            <ng-template #s25ModalBody>
                @if (overlapData(); as overlaps) {
                    <p>
                        There are {{ overlaps.length }} outstanding sets of overlapping sections which need to be moved
                        before sending to optimizer.
                    </p>
                    <p>Overlapping sections are marked with blue borders in the grid.</p>
                    @for (overlap of overlaps; track $index) {
                        <div class="overlapData">
                            <label>Location:</label>
                            <span>{{ overlap.roomName }}</span>
                            <label>Meeting Pattern:</label>
                            <span>{{ overlap.dow }}</span>
                            <label>Time:</label>
                            <span>{{ overlap.startTime }} - {{ overlap.endTime }}</span>
                            <label>Section A:</label>
                            <span>{{ overlap.sectionA }}</span>
                            <label>Section B:</label>
                            <span>{{ overlap.sectionB }}</span>
                        </div>
                    }
                }
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #confirmOverlap [title]="'Warning: Overlapping Sections'" [type]="'confirm'" [size]="'xs'">
            <ng-template #s25ModalBody>
                <div class="confirmOverlap">
                    <p>
                        You are attempting to move a section into a room that is currently occupied by another section.
                        You will need to relocate at least one of these sections prior to sending your results to the
                        Optimizer.
                    </p>

                    <p class="ngBold">Do you wish to continue?</p>

                    @if (overlapData()?.[0]; as overlap) {
                        <div class="overlapData">
                            <label>Location:</label>
                            <span>{{ overlap.roomName }}</span>
                            <label>Meeting Pattern:</label>
                            <span>{{ overlap.dow }}</span>
                            <label>Time:</label>
                            <span>{{ overlap.startTime }} - {{ overlap.endTime }}</span>
                            <label>Section A:</label>
                            <span>{{ overlap.sectionA }}</span>
                            <label>Section B:</label>
                            <span>{{ overlap.sectionB }}</span>
                        </div>
                    }
                </div>
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #confirmUndersizedRoom [title]="'Warning: Undersized Location'" [type]="'confirm'" [size]="'xs'">
            <ng-template #s25ModalBody>
                @if (undersizedRoomData(); as undersizedRoom) {
                    <div class="confirmUndersizedRoom">
                        <p>You are attempting to move a section into a location which cannot support its headcount.</p>

                        <p class="ngBold">Do you wish to continue?</p>

                        <div class="undersizedRoomData">
                            <label>Section:</label>
                            <span>{{ undersizedRoom.event.mpg.name }}</span>
                            <label>Headcount:</label>
                            <span>{{ undersizedRoom.event.mpg.headCount }}</span>
                            <label>Location:</label>
                            <span>{{ undersizedRoom.room.mpg.name }}</span>
                            <label>Max Capacity:</label>
                            <span>{{ undersizedRoom.room.mpg.maxCapacity }}</span>
                        </div>
                    </div>
                }
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        s25-ng-loading-inline-static {
            text-align: center;
        }

        .overlapItem {
            border: 2px dashed #03f;
            height: 100%;
            z-index: 10;
            position: relative;
        }

        .board-name {
            text-align: center;
            margin: 0;
            padding: 0.5em;
        }

        .share-modal {
            min-height: min(30rem, 75svh);
        }

        .share-modal .users {
            display: grid;
            gap: 0.5rem;
            grid-template-columns: 1fr 5rem;
        }

        .share-modal .shareWith {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
            padding-bottom: 1rem;
        }

        .share-modal .shareWith s25-ng-dropdown-search-criteria {
            flex: 1;
        }

        .filters {
            justify-content: space-between;
            padding-bottom: 0.5em;
            padding-top: 1.5em;
        }

        .filters,
        .filters .options,
        .filters .options .name,
        .filters .options .capacity,
        .filters .buttons {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .filters .options {
            gap: 1em;
            row-gap: 1.5rem;
        }

        .toggle-filters {
            width: 14.5em;
            display: flex;
            justify-content: space-between;
        }

        .filters .capacity-value {
            width: 4em;
        }

        .filters :has(> label.above) {
            position: relative;
        }

        .filters label.above {
            font-size: 0.9em;
            text-align: center;
            position: absolute;
            translate: 0 -100%;
            width: 100%;
            padding-bottom: 0.25em;
        }

        .filters .capacity-value input {
            width: 100%;
            padding-left: 0.25em;
            padding-right: 0;
        }

        button {
            min-width: fit-content;
        }

        .overlapData,
        .undersizedRoomData {
            display: grid;
            grid-template-columns: 10em auto;
            margin-top: 1em;
            padding: 0.5em;
            border: 1px solid black;
            background: #eee;
        }

        .overlapData label,
        .undersizedRoomData label {
            font-weight: bold;
        }

        ::ng-deep .nm-party--on s25-ng-meeting-pattern-grid .overlapData,
        ::ng-deep .nm-party--on s25-ng-meeting-pattern-grid .undersizedRoomData {
            background: #222;
        }

        .confirmOverlap,
        .confirmUndersizedRoom {
            display: grid;
            gap: 1em;
        }

        .confirmOverlap .overlapData,
        .confirmUndersizedRoom .undersizedRoomData {
            margin: 0;
        }

        .row-header {
            padding-left: 0.5em;
        }

        .ngAnchor {
            display: initial;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineStaticComponent,
        S25VirtualGridModule,
        TypedTemplateDirective,
        NgTemplateOutlet,
        S25IconComponent,
        S25ButtonComponent,
        FormsModule,
        S25MultiselectModule,
        S25ItemComponent,
        S25MeetingPatternGridEventComponent,
        S25DropdownPaginatedModule,
        S25ModalModule,
    ],
})
export class S25MeetingPatternGridComponent {
    readonly board = input.required<SwarmSchedule.BoardI>();

    // Emitting unloads the component. Boolean indicates whether to refresh the table in the parent.
    readonly exit = output<boolean>();

    // Template views
    private readonly shareModal = viewChild("shareModal", { read: S25ModalComponent });
    protected readonly overlapModal = viewChild("overlapModal", { read: S25ModalComponent });
    protected readonly confirmOverlap = viewChild("confirmOverlap", { read: S25ModalComponent });
    protected readonly confirmUndersizedRoom = viewChild("confirmUndersizedRoom", { read: S25ModalComponent });
    readonly dowGrid =
        viewChild<S25DowGridComponent<MPG.Row, MPG.SourceItem, DowGrid.ProjectionData, DowGrid.DecorationData>>(
            S25DowGridComponent,
        );

    protected readonly optionsLeftTmpl = viewChild<string, TemplateRef<DowGrid.WrappedOptionsTemplateContext>>(
        "optionsLeft",
        {
            read: TemplateRef,
        },
    );
    protected readonly optionsRightTmpl = viewChild<string, TemplateRef<DowGrid.OptionsTemplateContext>>(
        "optionsRight",
        {
            read: TemplateRef,
        },
    );
    protected readonly optionsBelowTemplate = viewChild<string, TemplateRef<object>>("optionsBelow", {
        read: TemplateRef,
    });
    protected readonly optionsBelowTmpl = computed(
        () => this.optionsBelowTemplate() as unknown as TemplateRef<void> | undefined,
    );
    protected readonly defaultRowHeaderTmpl = viewChild<string, TemplateRef<Grid.HeaderTemplateContext<MPG.Row>>>(
        "defaultRowHeaderTemplate",
        { read: TemplateRef },
    );
    protected readonly defaultItemTmpl = viewChild<
        string,
        TemplateRef<Grid.ItemProjectionTemplateContext<MPG.Item, DowGrid.ProjectionData>>
    >("defaultItemTemplate", { read: TemplateRef });
    protected readonly decorationTmpl = viewChild<
        string,
        TemplateRef<DowGrid.DecorationTemplateContext<MPG.Item, DowGrid.ProjectionData>>
    >("decorationTemplate", { read: TemplateRef });

    // Constants
    private static nextId = InstanceCount.idGenerator({ prefix: "MeetingPatternGrid" });
    private static readonly DEFAULT_POLL_INTERVAL = 2_000;
    protected readonly uniqueId = S25MeetingPatternGridComponent.nextId();
    protected readonly Item = Item;
    protected readonly BOARD_CONST = BOARD_CONST;

    // Injectables
    private readonly loginStore = inject(LoginStore);
    private readonly userStore = inject(UserStore);
    private readonly destroyRef = inject(DestroyRef);

    // Resources
    private readonly prefsResource = s25Resource({ loader: MeetingPatternGridService.getChartPreferences });
    private readonly boardResource = s25Resource({
        params: this.board,
        loader: ({ params: board }) => BoardUploadService.createBoardFromOptimizer(board),
    });
    protected readonly status = s25Resource.combine(this.prefsResource, this.boardResource);

    // Editable resource-backed grid state
    /**
     * Seeding builds each item's own signals and reads them back. Left tracked, those reads would make
     * this computed a consumer of the very state the grid writes to, so committing a move would
     * invalidate the seed and rebuild every item from the board payload — throwing the move away.
     */
    private readonly data = computed(() => {
        const root = this.boardResource.value()?.root;
        return untracked(() => this.mapBoardToGridData(root));
    });
    protected readonly rows = linkedSignal(() => {
        const rows = [...this.data().rows];
        const relevanceSortItem = this.relevanceSortItem();
        if (relevanceSortItem) {
            const scores = MPGUtil.scoreRows(rows, relevanceSortItem, this.minFillRatio());
            rows.sort((firstRow, secondRow) => scores.get(secondRow.id)! - scores.get(firstRow.id)!);
        }
        return rows;
    });
    protected readonly items = linkedSignal(() => this.data().items as MPG.Item[]);
    protected readonly errorMessage = computed(() =>
        this.boardResource.error() ? S25Util.errorText(this.boardResource.error()) : undefined,
    );
    readonly boardData = computed(() => this.boardResource.value()?.root);
    readonly rowIdToIndex = computed(() => new Map(this.rows().map((row, index) => [row.id, index])));
    readonly itemById = computed(() => new Map(this.items().map((item) => [item.id, item])));
    readonly minFillRatio = computed(() => this.boardData()?.minFillRatio ?? 0);
    readonly canDragHorizontally = computed(() => !!this.boardData()?.allowXDrag && this.board().mpgPushback);
    private readonly rowIndexMap = computed(() => new Map(this.rows().map((row, index) => [row.id, index])));
    private readonly appliedFilter = signal({
        name: "",
        capacityMin: undefined as number | undefined,
        capacityMax: undefined as number | undefined,
        features: new Set<number>(),
        partitions: new Set<number>(),
    });

    // Pure computes
    readonly startHour = computed(() => this.prefsResource.value()?.startHour ?? 0);
    readonly endHour = computed(() => this.prefsResource.value()?.endHour ?? 24);
    readonly visibleDows = computed(() => this.prefsResource.value()?.dows ?? []);

    // Mutable UI state
    readonly lastMessage = signal<EpochTimestamp>(Date.now()); // Including our own messages
    readonly pollInterval = signal<Milliseconds>(S25MeetingPatternGridComponent.DEFAULT_POLL_INTERVAL);
    readonly modalSharedUsers = signal<SwarmSchedule.BoardI["sharedUsers"]>([]);
    readonly sharing = linkedSignal(() => !!this.board().shared || !!this.board().sharedUsers?.length);
    readonly loading = signal({
        filter: false,
        sort: false,
        save: false,
    });
    readonly filters = signal({
        show: false,
        name: "",
        capacityMin: undefined as number | undefined,
        capacityMax: undefined as number | undefined,
        features: [] as S25ItemI[],
        partitions: [] as S25ItemI[],
    });
    readonly sortOrder = signal<"default" | "relevance">("default");
    readonly relevanceSortItem = signal<MPG.Item | undefined>(undefined);
    readonly overlapData = signal<
        | {
              roomName: string;
              dow: string;
              startTime: string;
              endTime: string;
              sectionA: string;
              sectionB: string;
          }[]
        | undefined
    >(undefined);
    readonly undersizedRoomData = signal<
        | {
              event: MPG.Item;
              room: MPG.Row;
          }
        | undefined
    >(undefined);

    readonly interactionHandlers: DowGrid.InteractionHandlers<MPG.Item> = {
        onPickup: (context) => this.onPickup(context.primary, context.participants),
        onDragChanged: (context) => this.onDragChanged(context.primary, context.participants),
        onDrop: (context) => this.onDrop(context.primary, context.participants),
        onCancel: (context) => void this.onMoveCancelled(context.participants),
        onCommit: (context) => this.onCommit(context.primary, context.participants),
        onUndo: (context) => this.onUndoRedo(context.primary, context.participants),
        onRedo: (context) => this.onUndoRedo(context.primary, context.participants),
    };

    readonly domainEquals = MPG.positionsEqual;

    constructor() {
        effect((onCleanup) => {
            if (!this.sharing() || !this.boardData() || !this.userStore.username() || !this.loginStore.sessionId()) {
                return;
            }
            const intervalId = window.setInterval(() => {
                void this.forcePoll();
            }, this.pollInterval());
            onCleanup(() => window.clearInterval(intervalId));
        });
        this.destroyRef.onDestroy(() => this.invalidateCache());

        effect(() => {
            const filter = this.appliedFilter();
            for (const row of this.rows()) row.mpg.hidden.set(!this.applyFiltersToRow(row, filter));
        });
    }

    invalidateCache() {
        CacheRepository.invalidateByMethod("BoardUploadService.createBoardFromOptimizer");
    }

    private mapBoardToGridData(
        boardData: Awaited<ReturnType<typeof BoardUploadService.createBoardFromOptimizer>>["root"] | undefined,
    ): MPG.Data {
        if (!boardData) return { rows: [], items: [] };

        const rows = boardData.room.map((room) => MPGUtil.mapLocationToRow(room)).sort(this.sortByIdAndName);
        const uniqueEvents = MeetingPatternGridService.getUniqueEvents(boardData.item);
        const items = MeetingPatternGridService.getItems(null, uniqueEvents).map((event): MPG.SourceItem => {
            const { position, ...mpg } = MPGUtil.getItemData(event);
            return {
                id: event.uuid!,
                position,
                mpg,
                dowGrid: {
                    noShadows: mpg.noShadows,
                },
                draggable: signal(event.draggable),
                minimapColor: signal("#cff3d2"),
            };
        });

        return { rows, items };
    }

    @Bind
    onGridItemInitialized(item: DowGrid.Item<MPG.SourceItem>): MPG.Item {
        const asInitialized = item as MPG.Item;
        if (item.mpg.initialized) return asInitialized;

        const candidate = computed(() => {
            const position = item.dowGrid.candidate.position();
            if (!position) return undefined;
            const offset = item.dowGrid.candidate.offset();
            if (!offset) return position;

            const top = this.calculateTopValue(item.position().roomId, this.rowIdToIndex()) + offset.y;
            const row = MPGUtil.atPercentClosest(this.rows(), top);
            return {
                roomId: row.mpg.roomId,
                dow: position.dow,
                startHour: position.startHour,
                endHour: position.endHour,
            };
        });
        const extras = {
            candidate,
            effective: computed(() => candidate() ?? item.position()),
        } satisfies Omit<MPG.Item["mpg"], keyof MPG.SourceItem["mpg"]>;

        Object.assign(asInitialized.mpg, extras);
        asInitialized.mpg.initialized = true;
        return asInitialized;
    }

    @Bind
    itemProjector(item: MPG.Item): readonly DowGrid.ItemProjectionDefinition[] {
        const placement = computed(() => {
            const position = item.mpg.effective();
            const offset = item.dowGrid.candidate.offset();
            const roomId = offset ? item.position().roomId : position.roomId;
            const rowIndex = this.rowIdToIndex();
            return {
                dow: position.dow,
                startHour: position.startHour,
                endHour: position.endHour,
                top: this.calculateTopValue(roomId, rowIndex) + (offset?.y ?? 0),
                height: rowIndex.size ? 100 / rowIndex.size : 0,
            };
        });
        return DowGridUtil.primaryProjection(item, {
            placement,
            is24Hours: this.userStore.is24Hours,
        });
    }

    @Bind
    dragParticipantsResolver(primary: MPG.Item, items: readonly MPG.Item[]): readonly MPG.Item[] {
        return MPGUtil.syncedItems(primary, items);
    }

    @Bind
    async onPickup(item: MPG.Item, items: readonly MPG.Item[]): Promise<boolean> {
        MPGUtil.updateItemTimestamps(items);
        const itemTos = MPGUtil.getItemTos(items);
        return MeetingPatternGridService.lockItems(this.board().boardUUID, itemTos);
    }

    @Throttle(300, true)
    onDragChanged(item: MPG.Item, items: readonly MPG.Item[]): void {
        MPGUtil.updateItemTimestamps(items);
        const itemTos = MPGUtil.getItemTos(items);
        MeetingPatternGridService.dispatch(this.board().boardUUID, itemTos);

        AriaLive.announce(this.positionMessage(item.mpg.effective()), true);
    }

    positionMessage(position: MPG.Position): string {
        const time = S25Util.date.ariaTimeString(
            S25Util.date.toTimeStrFromHours(position.startHour, this.userStore.is24Hours()),
        );

        const roomName = this.rowByRoomId(position.roomId)?.mpg.name ?? position.roomId;
        const dowMessage = position.dow
            .split("")
            .map((char: keyof typeof S25Const.dowCharToName) => S25Const.dowCharToName[char])
            .join(" ");
        return `${time} on ${dowMessage} in ${roomName}`;
    }

    doItemsOverlap(first: MPG.Item, second: MPG.Item): boolean {
        return MPGUtil.itemsOverlap(first, second);
    }

    @Bind
    async onDrop(item: MPG.Item, items: readonly MPG.Item[]): Promise<boolean> {
        MPGUtil.updateItemTimestamps(items);

        const valid = await this.validateNewPosition(items);
        if (!valid) return false;

        return this.onItemsMoved(items);
    }

    onCommit(item: MPG.Item, items: readonly MPG.Item[]) {
        this.applyCommittedMpg(items);
    }

    applyCommittedMpg(items: readonly MPG.Item[]) {
        for (const item of items) MPGUtil.applyCommittedMpg(item);
    }

    async onItemsMoved(items: readonly MPG.Item[]) {
        const itemTos = MPGUtil.getItemTos(items);
        return MeetingPatternGridService.unlockItems(this.board().boardUUID, itemTos);
    }

    async onMoveCancelled(items: readonly MPG.Item[]) {
        MPGUtil.updateItemTimestamps(items);
        const itemTos = MPGUtil.getCommittedItemTos(items);
        await MeetingPatternGridService.dispatch(this.board().boardUUID, itemTos);
        return MeetingPatternGridService.unlockItems(this.board().boardUUID, itemTos);
    }

    async validateNewPosition(items: readonly MPG.Item[]) {
        // Do not warn in UNASSIGNED row
        const roomId = items[0].mpg.effective().roomId;
        if (roomId === -1) return true;

        const roomItems = this.items().filter((item) => item.mpg.effective().roomId === roomId);
        for (const item of items) {
            for (const roomItem of roomItems) {
                if (item.id === roomItem.id) continue;
                const overlap = this.doItemsOverlap(item, roomItem);
                if (!overlap) continue;

                this.overlapData.set([this.getOverlappingData([item, roomItem])]);

                const ok = await this.confirmOverlap()?.open();
                if (!ok) return false;
            }
        }

        for (const item of items) {
            const room = this.rowByRoomId(item.mpg.effective().roomId);
            if (!room || item.mpg.headCount <= room.mpg.maxCapacity) continue;
            this.undersizedRoomData.set({ event: item, room });

            const ok = await this.confirmUndersizedRoom()?.open();
            if (!ok) return false;
        }

        return true;
    }

    @Bind
    async poll(): Promise<MPG.PollData> {
        const messages = await MeetingPatternGridService.poll(this.board().boardUUID);
        const pollData: MPG.PollData = { items: {} };
        for (const message of messages) this.processMessage(pollData, message);

        // Backoff polling if there's no activity
        // For every 10 minutes of inactivity, double the poll interval, maxing out around 1 minute, after 50 minutes
        const minutes = (Date.now() - this.lastMessage()) / S25Const.ms.min;
        const exp = Math.min(Math.floor(minutes / 10), 5);
        this.pollInterval.set(S25MeetingPatternGridComponent.DEFAULT_POLL_INTERVAL * Math.pow(2, exp));

        return pollData;
    }

    async forcePoll() {
        const pollData = await this.poll();
        this.applyPollData(pollData);
    }

    applyPollData(pollData: MPG.PollData | undefined): void {
        if (!pollData?.items) {
            pollData?.postPoll?.();
            return;
        }

        const nextItems: MPG.SourceItem[] = [...this.items()];
        let alteredItems = false;
        const itemById = new Map(nextItems.map((item) => [item.id, item]));
        for (const [itemId, itemUpdate] of Object.entries(pollData.items)) {
            if (itemUpdate.delete) {
                const itemIndex = nextItems.findIndex((item) => item.id === itemId);
                if (itemIndex >= 0) nextItems.splice(itemIndex, 1);
                alteredItems = true;
                continue;
            }
            if (itemUpdate.create) {
                nextItems.push(itemUpdate.create);
                itemById.set(itemUpdate.create.id, itemUpdate.create);
                alteredItems = true;
            }

            const item = itemById.get(itemId) as unknown as MPG.Item | undefined;
            if (!item) continue;
            if (itemUpdate.moveTo) {
                item.position.set(itemUpdate.moveTo);
                const dowGrid = this.dowGrid();
                // Until the grid renders there is no tile to animate, so the position write above is enough
                if (dowGrid) void dowGrid.moveItem(item);
            }
            itemUpdate.updateItem?.(item);
        }
        if (alteredItems) this.items.set(nextItems as MPG.Item[]);
        pollData.postPoll?.();
    }

    sortByIdAndName(a: MPG.Row, b: MPG.Row) {
        if (a.id === "-1" && b.id !== "-1") return -1;
        if (b.id === "-1" && a.id !== "-1") return 1;
        if (a.mpg.name.toLowerCase() < b.mpg.name.toLowerCase()) return -1;
        if (a.mpg.name.toLowerCase() > b.mpg.name.toLowerCase()) return 1;
        return 0;
    }

    onSortByRelevance(item: MPG.Item) {
        this.sortOrder.set("relevance");
        this.relevanceSortItem.set(item);
    }

    calculateTopValue(rowId: number | string, rowIdToIndex: Map<string, number>): number {
        return ((rowIdToIndex.get(String(rowId)) ?? 0) / rowIdToIndex.size) * 100;
    }

    rowByRoomId(roomId: number): MPG.Row | undefined {
        const index = this.rowIdToIndex().get(String(roomId));
        return index == null ? undefined : this.rows()[index];
    }

    processMessage(pollData: MPG.PollData, message: SwarmSchedule.Message): void {
        this.lastMessage.set(Date.now());
        if ("sessionId" in message && message.sessionId === this.loginStore.sessionId()) return;
        if (message.item.itemUUID === `unshare-${this.userStore.username()}`) this.exit.emit(true);
        switch (message.category) {
            case "default":
                return this.processDefaultMessage(pollData, message as SwarmSchedule.DefaultMessage);
            case "user":
                return this.processUserMessage(pollData, message as SwarmSchedule.UserMessage);
        }
    }

    processDefaultMessage(pollData: MPG.PollData, message: SwarmSchedule.DefaultMessage): void {
        const item = this.itemById().get(message.item.itemUUID);
        if (!item) return; // Unknown item

        if (message.item.timestamp <= item.mpg.lastChange) return; // Ignore superseded or already executed changes
        item.mpg.lastChange = message.item.timestamp;

        pollData.items![message.item.itemUUID] = {
            moveTo: {
                roomId: message.item.roomId,
                dow: message.item.dow,
                startHour: S25Util.date.dateToHours(message.item.start),
                endHour: S25Util.date.dateToHours(message.item.end),
            },
        };

        AriaLive.announce(`${item.mpg.name} was moved to ${this.positionMessage(item.position())}`, true);
    }

    /**
     * Processes a user type message from the meeting pattern grid service
     * @return {void} Nothing, because we set the data directly on the item
     */
    processUserMessage(pollData: MPG.PollData, message: SwarmSchedule.UserMessage): void {
        if (message.username === this.userStore.username()) return;

        const item = this.itemById().get(message.item.itemUUID) as unknown as MPG.Item;
        if (!item) return;
        // Set or reset the active user upon user-on/user-off messages
        item.mpg.activeUser.set(message.type === "user-on" ? message.username : undefined);
    }

    openShareModal() {
        this.modalSharedUsers.set([...this.board().sharedUsers]);
        this.shareModal()?.open();
    }

    onShareSave() {
        // Share and unshare as needed
        const board = this.board();
        const pre = new Set(board.sharedUsers.map((user) => user.username));
        const post = new Set(this.modalSharedUsers().map((user) => user.username));
        for (const user of pre) {
            if (!post.has(user)) BoardService.unshareBoard(board.boardId, board.boardUUID, user);
        }
        for (const user of post) {
            if (!pre.has(user)) BoardService.shareBoard(board.boardId, board.boardUUID, user);
        }
        board.sharedUsers = this.modalSharedUsers();
        this.sharing.set(!!board.shared || !!board.sharedUsers.length);
        this.shareModal()?.close();
    }

    addSharedUser(user: { itemName: string }) {
        const alreadyShared = this.modalSharedUsers().some((item) => item.username === user.itemName);
        if (!alreadyShared) this.modalSharedUsers.update((users) => [...users, { username: user.itemName }]);
    }

    removeSharedUser(index: number) {
        this.modalSharedUsers.update((users) => users.filter((_, userIndex) => userIndex !== index));
    }

    resetSort() {
        this.sortOrder.set("default");
        this.relevanceSortItem.set(undefined);
    }

    updateFilter(
        key: "name" | "capacityMin" | "capacityMax" | "features" | "partitions",
        value: string | number | S25ItemI[] | undefined,
    ) {
        this.filters.update((filters) => ({ ...filters, [key]: value }) as typeof filters);
    }

    runFilters() {
        const filters = this.filters();
        const features = new Set(filters.features.map((feature) => Number(feature.itemId)));
        const partitions = new Set(filters.partitions.map((partition) => Number(partition.itemId)));
        const filterData = {
            name: filters.name.toLowerCase(),
            capacityMin: filters.capacityMin,
            capacityMax: filters.capacityMax,
            features,
            partitions,
        };
        this.appliedFilter.set(filterData);
    }

    applyFiltersToRow(
        row: MPG.Row,
        data: {
            name: string;
            capacityMin: number | undefined;
            capacityMax: number | undefined;
            features: Set<number>;
            partitions: Set<number>;
        },
    ) {
        if (row.id === "-1") return true; // Unassigned row is always present
        if (row.mpg.name && !row.mpg.name.toLowerCase().includes(data.name)) return false;
        if (data.capacityMin && row.mpg.maxCapacity < data.capacityMin) return false;
        if (data.capacityMax && row.mpg.maxCapacity > data.capacityMax) return false;
        if (data.features.size && !row.mpg.features.some((feature) => data.features.has(feature.id))) return false;
        if (data.partitions.size && (!row.mpg.partition || !data.partitions.has(row.mpg.partition.id))) return false;
        return true;
    }

    clearFilters() {
        if (this.loading().filter) return;
        const currentFilters = this.filters();
        currentFilters.features.splice(0);
        currentFilters.partitions.splice(0);
        this.filters.update((filters) => ({
            ...filters,
            name: "",
            capacityMin: undefined,
            capacityMax: undefined,
        }));
        this.runFilters();
    }

    setLoading(type: "filter" | "sort" | "save", isLoading: boolean) {
        this.loading.update((loading) => ({ ...loading, [type]: isLoading }));
    }

    startLoading(type: "filter" | "sort" | "save") {
        this.setLoading(type, true);
    }

    stopLoading(type: "filter" | "sort" | "save") {
        this.setLoading(type, false);
    }

    @Bind
    getOverlappingData(section: MPG.Item[]) {
        const [a, b] = section;
        const aPos = a.position();
        const bPos = b.position();

        const room = this.rowByRoomId(aPos.roomId)!;
        const start = Math.max(aPos.startHour, bPos.startHour);
        const end = Math.min(aPos.endHour, bPos.endHour);

        return {
            roomName: room.mpg.name,
            dow: aPos.dow,
            startTime: S25Util.date.toTimeStrFromHours(start, this.userStore.is24Hours()),
            endTime: S25Util.date.toTimeStrFromHours(end, this.userStore.is24Hours()),
            sectionA: a.mpg.name,
            sectionB: b.mpg.name,
        };
    }

    @Telemetry({ category: "MPG", type: "OptimizerSend" })
    @Bind
    async onSave() {
        AriaLive.announce("Saving", true);
        this.startLoading("save");

        let [ok, error] = await S25Util.Maybe(this.forcePoll()); // Make sure we have the latest data
        if (error) {
            S25Util.showError(error);
            this.stopLoading("save");
            return;
        }

        const overlap = this.findOverlappingSections();
        if (overlap.length) {
            this.stopLoading("save");
            this.overlapData.set(overlap.map(this.getOverlappingData));
            await this.overlapModal()?.open();
            this.overlapData.set(undefined);
            return;
        }

        const boardData = this.boardData();
        if (!boardData) {
            this.stopLoading("save");
            return;
        }
        const model = {
            boardId: boardData.boardId,
            boardUUID: boardData.boardUUID,
            origResults: boardData.origResults,
            origResultsMap: boardData.origResultsMap,
            items: this.items().map((item) => {
                const itemData = item.mpg;
                const position = item.position();
                const event = itemData.event;
                return {
                    origDow: event.origDow,
                    origStartTime: event.origStartTime,
                    itemName: event.itemName,
                    roomId: position.roomId,
                    eventId: event.eventId,
                    profileId: event.profileId,
                    groupId: event.groupId,
                    linkedItems: event.linkedItems,
                    moved: event.moved,
                    newpattern: event.newpattern,
                    objRef: event.objRef,
                    dow: position.dow,
                    start: new Date(
                        S25Util.date.toS25ISODateTimeStr(S25Util.date.toTimeStrFromHours(position.startHour))!,
                    ),
                    startHour: position.startHour,
                    startTime: S25Util.date.toS25ISOTimeStrFromHours(position.startHour),

                    end: new Date(S25Util.date.toS25ISODateTimeStr(S25Util.date.toTimeStrFromHours(position.endHour))!),
                    endHour: position.endHour,
                    endTime: S25Util.date.toS25ISOTimeStrFromHours(position.endHour),
                    profileCode: itemData.profileCode,
                    daysOfWeek: position.dow.split(""),
                    occs: itemData.occs,
                };
            }),
        } as unknown as SwarmSchedule.ModelI;

        [ok, error] = await S25Util.Maybe(BoardService.saveBoard(model));
        if (error) S25Util.showError(error);
        this.stopLoading("save");
        AriaLive.announce("Finished saving", true);
    }

    toggleFilters() {
        this.filters.update((filters) => ({ ...filters, show: !filters.show }));
    }

    findOverlappingSections() {
        const sections: MPG.Item[][] = [];

        const items = this.items();
        // Group by location and DOW
        const groups = S25Util.array.groupBy(items, (item) => `${item.position().roomId} - ${item.position().dow}`);
        for (const group of Object.values(groups)) {
            if (!group) continue;
            if (group[0].position().roomId === -1) continue; // Ignore unassigned row
            group.sort((a, b) => {
                const aPos = a.position();
                const bPos = b.position();
                return (
                    BOARD_CONST.allDows.indexOf(aPos.dow) - BOARD_CONST.allDows.indexOf(bPos.dow) ||
                    aPos.startHour - bPos.startHour ||
                    aPos.endHour - bPos.endHour
                );
            });

            for (const i of S25Util.range(group.length)) {
                const item = group[i];
                const itemEnd = item.position().endHour;

                for (const j of S25Util.range(i + 1, group.length)) {
                    const otherItem = group[j];
                    if (otherItem.position().startHour >= itemEnd) break;

                    const overlap = this.doItemsOverlap(item, otherItem);
                    if (overlap) {
                        sections.push([item, otherItem]);
                    }
                }
            }
        }

        return sections;
    }

    @Bind
    async onUndoRedo(item: MPG.Item, items: readonly MPG.Item[]) {
        return this.onDrop(item, items);
    }
}

export namespace MPG {
    export type Data = { rows: Row[]; items: SourceItem[]; message?: string };
    export type Row = Grid.SourceHeader & { mpg: RowData };
    export type SourceItem = DowGrid.SourceItem<Position> & { mpg: ItemData };
    export type Item = DowGrid.Item<SourceItem> & {
        position: WritableSignal<Position>;
        mpg: SourceItem["mpg"] & {
            candidate: Signal<Position | undefined>;
            effective: Signal<Position>;
        };
    };

    export type Position = {
        roomId: number;
        dow: string;
        startHour: number;
        endHour: number;
    };

    export function positionsEqual(left: Position, right: Position): boolean {
        return (
            left.roomId === right.roomId &&
            left.dow === right.dow &&
            left.startHour === right.startHour &&
            left.endHour === right.endHour
        );
    }

    export type PollData = {
        items?: Record<
            string,
            {
                moveTo?: MPG.Position;
                delete?: boolean;
                create?: SourceItem;
                updateItem?: (item: Item) => void;
            }
        >;
        postPoll?: () => void;
    };

    export type RowData = {
        name: string;
        features: { id: number; name: string }[];
        partition?: { id: number; name: string };
        maxCapacity: number;
        hidden: WritableSignal<boolean>;
        roomId: number;
    };

    export type ItemData = {
        initialized: boolean;
        name: string;
        event: MeetingPatternGrid.Item;
        profileCode: MeetingPatternGrid.Item["profileCode"];
        uuid: MeetingPatternGrid.Item["uuid"];
        sourceRoomId: MeetingPatternGrid.Item["roomId"];
        visible?: boolean;
        organization?: {
            id: number;
            name: string;
            partitions: {
                priority: number;
                list: { id: number; name: string }[];
            }[];
        };
        features: { id: number; name: string }[];
        headCount: number;
        activeUser: WritableSignal<string | undefined>;
        lastChange: Proto.EpochTimestamp;
        broughtToFront?: boolean;
        occs: MeetingPatternGrid.Item["occs"];
        noShadows: WritableSignal<boolean>;
    };
}
